import React, { useEffect } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { useSessionTracking, usePageTracking } from "./features/analytics/useTracking";
import posthog from 'posthog-js'
import ProtectedRoute from "./components/ProtectedRoute";
import PublicShell from "./components/PublicShell";
import UpgradePromptProvider from "./components/UpgradePromptProvider";
import SellerRoute from "./components/SellerRoute";
import AdminLayout from "./components/admin/AdminLayout";
import AdminHubTabs, { HUB_TABS } from "./components/admin/AdminHubTabs";
import AdminRoute from "./components/admin/AdminRoute";
import { AuthProvider } from "./context/AuthContext";
import BuyerOnboardingPage from "./pages/BuyerOnboardingPage";
import RecommendedFeedPage from "./pages/RecommendedFeedPage";
import AddNewsPage from "./pages/AddNewsPage";
import CreateDealPage from "./pages/CreateDealPage";
import ContactPage from "./pages/ContactPage";
import DealsPage from "./pages/DealsPage";
import DirectChatPage from "./pages/DirectChatPage";
import EnquiriesPage from "./pages/EnquiriesPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MyInventoryPage from "./pages/MyInventoryPage";
import BoostWizardPage from "./pages/boost/BoostWizardPage";
import BoostCampaignsPage from "./pages/boost/BoostCampaignsPage";
import BoostAnalyticsPage from "./pages/boost/BoostAnalyticsPage";
import NotificationsPage from "./pages/NotificationsPage";
import CommunityPage from "./pages/CommunityPage";
import ProfilePage from "./pages/ProfilePage";
import RagChatPage from "./pages/RagChatPage";
import PartnerProfilePage from "./pages/PartnerProfilePage";
import SellerOnboardingPage from "./pages/SellerOnboardingPage";
import ShowcasePage from "./pages/ShowcasePage";
import WebinarPage from "./pages/WebinarPage";
import WebinarsNewsPage from "./pages/WebinarsNewsPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminCommunityPage from "./pages/admin/AdminCommunityPage";
import AdminEnquiriesPage from "./pages/admin/AdminEnquiriesPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminDealsPage from "./pages/admin/AdminDealsPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminPartnersPage from "./pages/admin/AdminPartnersPage";
import AdminPartnerProfilePage from "./pages/admin/AdminPartnerProfilePage";
import AdminWebinarsPage from "./pages/admin/AdminWebinarsPage";
import AdminNewsPage from "./pages/admin/AdminNewsPage";
import AdminPollsPage from "./pages/admin/AdminPollsPage";
import AdminActivityPage from "./pages/admin/AdminActivityPage";
import AdminAiGenerationsPage from "./pages/admin/AdminAiGenerationsPage";
import AdminAiSearchUsagePage from "./pages/admin/AdminAiSearchUsagePage";
import AdminMarketPulsePage from "./pages/admin/AdminMarketPulsePage";
import AdminSubscriptionsPage from "./pages/admin/AdminSubscriptionsPage";
import AdminPricingPage from "./pages/admin/AdminPricingPage";
import AdminPromotionsPage from "./pages/admin/AdminPromotionsPage";
import AdminGamificationPage from "./pages/admin/AdminGamificationPage";
import AdminPaymentGatewayPage from "./pages/admin/AdminPaymentGatewayPage";
import AdminPlansPricesPage from "./pages/admin/AdminPlansPricesPage";
import AdminCouponsPage from "./pages/admin/AdminCouponsPage";
import AdminInvoicesPage from "./pages/admin/AdminInvoicesPage";
import AdminInvoiceDesignPage from "./pages/admin/AdminInvoiceDesignPage";
import AdminPlanLimitsPage from "./pages/admin/AdminPlanLimitsPage";
import AdminChipsPage from "./pages/admin/AdminChipsPage";
import AdminPromoBannersPage from "./pages/admin/AdminPromoBannersPage";
import AdminRecommendationsPage from "./pages/admin/AdminRecommendationsPage";
import AdminCategoriesPage from "./pages/admin/AdminCategoriesPage";
import AdminHomeDealBannersPage from "./pages/admin/AdminHomeDealBannersPage";
import AdminOnboardingPage from "./pages/admin/AdminOnboardingPage";
import AdminLegalDocsPage from "./pages/admin/AdminLegalDocsPage";
import AdminNotificationsPage from "./pages/admin/AdminNotificationsPage";
import AdminNotificationOpsPage from "./pages/admin/AdminNotificationOpsPage";
import AdminTemplatesPage from "./pages/admin/AdminTemplatesPage";
import AdminPromptPlaygroundPage from "./pages/admin/AdminPromptPlaygroundPage";
import AdminBoostPage from "./pages/admin/AdminBoostPage";
import AdminWhatsAppPage from "./pages/admin/AdminWhatsAppPage";
import AdminVoiceAgentPage from "./pages/admin/AdminVoiceAgentPage";
import AdminEmailPage from "./pages/admin/AdminEmailPage";
import AdminGalleryReviewPage from "./pages/admin/AdminGalleryReviewPage";
import AdminGstVerificationPage from "./pages/admin/AdminGstVerificationPage";
import AdminLastMinuteReviewPage from "./pages/admin/AdminLastMinuteReviewPage";
import AdminStuckDealsPage from "./pages/admin/AdminStuckDealsPage";
import AdminDeletedDealsPage from "./pages/admin/AdminDeletedDealsPage";
import AdminIndexingPage from "./pages/admin/AdminIndexingPage";
import AdminOrganizationsPage from "./pages/admin/AdminOrganizationsPage";
import AdminOrgClusteringPage from "./pages/admin/AdminOrgClusteringPage";
// Org-admin console — its own auth path, deliberately sharing nothing with the
// superadmin tree above (separate storage keys, separate route guard).
import OrgAdminRoute from "./components/org-admin/OrgAdminRoute";
import OrgAdminLayout from "./components/org-admin/OrgAdminLayout";
import OrgAdminLoginPage from "./pages/org-admin/OrgAdminLoginPage";
import OrgAdminMagicLoginPage from "./pages/org-admin/OrgAdminMagicLoginPage";
import OrgAdminDashboardPage from "./pages/org-admin/OrgAdminDashboardPage";
import OrgAdminCandidatesPage from "./pages/org-admin/OrgAdminCandidatesPage";
import OrgAdminMembersPage from "./pages/org-admin/OrgAdminMembersPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import TestSpeechPage from "./pages/TestSpeechPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CashfreePayPage from "./pages/CashfreePayPage";
import PricingPage from "./pages/PricingPage";
import NotFoundPage from "./pages/NotFoundPage";

import DmGlobalListener from "./components/DmGlobalListener";

// Admin now lives on its own host; devapp.gullivr.ai/admin/* forwards there so
// old links/bookmarks keep working instead of 404ing.
const SUPERADMIN_HOST = "superadmin.gullivr.ai";
// Hosts that only ever show /admin/*: prod superadmin + the Azure staging admin site.
const ADMIN_ONLY_HOSTS = [SUPERADMIN_HOST, "front-admin-preview.gullivr.in"];
const isSuperAdminHost = () =>
  typeof window !== "undefined" && ADMIN_ONLY_HOSTS.includes(window.location.hostname);

/** Redirect for an admin path that moved into a hub sub-tab. Carries the query
 *  string and hash over so deep links like ?deal_id=... survive the hop. */
const AdminPathRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// Must be rendered inside <Router> so useLocation works
const GullivrTracker: React.FC = () => {
  useSessionTracking();
  usePageTracking();
  return null;
};

function App() {
  const superAdminHost = isSuperAdminHost();

  // /admin/* on any other gullivr.ai host forwards to superadmin.gullivr.ai
  // (same path) so old devapp.gullivr.ai/admin links don't dead-end.
  useEffect(() => {
    const { hostname, pathname, search } = window.location;
    if (
      hostname.endsWith("gullivr.ai") &&
      hostname !== SUPERADMIN_HOST &&
      pathname.startsWith("/admin")
    ) {
      window.location.replace(`https://${SUPERADMIN_HOST}${pathname}${search}`);
    }
  }, []);

  // Track page views using react-router location
  const PageViewTracker: React.FC = () => {
    const loc = useLocation();
    useEffect(() => {
      try { posthog.capture('page_view', { path: loc.pathname }) } catch (e) {}
    }, [loc.pathname]);
    return null
  }
  return (
    <AuthProvider>
      <DmGlobalListener />
      <Router>
        <PageViewTracker />
        <GullivrTracker />
        <UpgradePromptProvider>
        <Routes>
          {superAdminHost ? null : (
          <>
          {/* Public auth entry */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* DealsPage renders its own full nav bar (MainNavbar), so it isn't wrapped in PublicShell */}
          <Route path="/" element={<DealsPage />} />
          {/* Public browsing — anonymous visitors allowed, nav shows Login / Sign up */}
          <Route element={<PublicShell />}>
            <Route path="/feed" element={<RecommendedFeedPage />} />
            <Route path="/chat" element={<RagChatPage />} />
            <Route path="/partners/:ownerId" element={<PartnerProfilePage />} />
          </Route>
          {/* Authenticated — account / personal routes redirect to /login when anonymous */}
          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding/seller" element={<SellerOnboardingPage />} />
            <Route path="/onboarding/buyer-preferences" element={<BuyerOnboardingPage />} />
            <Route element={<SellerRoute />}>
              <Route path="/inventory/my" element={<MyInventoryPage />} />
              <Route path="/boost/new" element={<BoostWizardPage />} />
              <Route path="/boost/campaigns" element={<BoostCampaignsPage />} />
              <Route path="/boost/campaigns/:id/analytics" element={<BoostAnalyticsPage />} />
              <Route path="/deals/create" element={<CreateDealPage />} />
              <Route path="/news/add" element={<AddNewsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/webinar" element={<WebinarPage />} />
              <Route path="/webinars-news" element={<WebinarsNewsPage />} />
              <Route path="/showcase" element={<ShowcasePage />} />
            </Route>
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/enquiries" element={<EnquiriesPage />} />
            <Route path="/dm" element={<DirectChatPage />} />
            <Route path="/dm/:threadId" element={<DirectChatPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/test-speech" element={<TestSpeechPage />} />
          </Route>

          {/* Subscription success — outside ProtectedRoute so Cashfree redirect always loads */}
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          {/* Cashfree mobile webview checkout — public so app webview can load it */}
          <Route path="/pay/cashfree" element={<CashfreePayPage />} />
          </>
          )}

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              {/* Standalone pages */}
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/gst-verification" element={<AdminGstVerificationPage />} />
              <Route path="/admin/intl-verification" element={<Navigate to="/admin/insights/dashboard" replace />} />
              <Route path="/admin/partners" element={<AdminPartnersPage />} />
              <Route path="/admin/partners/:ownerId" element={<AdminPartnerProfilePage />} />
              <Route path="/admin/enquiries" element={<AdminEnquiriesPage />} />

              {/* Organizations — directory on the base path, clustering tool as
                  a sub-tab, same shape as the deals hub. */}
              <Route path="/admin/organizations" element={<AdminHubTabs tabs={HUB_TABS.organizations} />}>
                <Route index element={<AdminOrganizationsPage />} />
                <Route path="clustering" element={<AdminOrgClusteringPage />} />
              </Route>

              {/* Hub pages — absorbed pages render as in-page sub-tabs */}
              <Route path="/admin/insights" element={<AdminHubTabs tabs={HUB_TABS.insights} />}>
                <Route index element={<Navigate to="/admin/insights/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboardPage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="activity" element={<AdminActivityPage />} />
                <Route path="ai-generations" element={<AdminAiGenerationsPage />} />
                <Route path="market-pulse" element={<AdminMarketPulsePage />} />
                <Route path="rag-chat" element={<AdminAiSearchUsagePage feature="rag_chat" />} />
                <Route path="market-research" element={<AdminAiSearchUsagePage feature="market_research" />} />
              </Route>

              {/* /admin/deals stays the base path so existing deep links and
                  notification click-throughs keep resolving to the deals list. */}
              <Route path="/admin/deals" element={<AdminHubTabs tabs={HUB_TABS.deals} />}>
                <Route index element={<AdminDealsPage />} />
                <Route path="last-minute-review" element={<AdminLastMinuteReviewPage />} />
                <Route path="gallery-review" element={<AdminGalleryReviewPage />} />
                <Route path="stuck-deals" element={<AdminStuckDealsPage />} />
                <Route path="deleted-by-seller" element={<AdminDeletedDealsPage />} />
                <Route path="indexing" element={<AdminIndexingPage />} />
              </Route>

              <Route path="/admin/channels" element={<AdminHubTabs tabs={HUB_TABS.channels} />}>
                <Route index element={<Navigate to="/admin/channels/notifications" replace />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
                <Route path="notification-ops" element={<AdminNotificationOpsPage />} />
                <Route path="whatsapp" element={<AdminWhatsAppPage />} />
                <Route path="voice-agent" element={<AdminVoiceAgentPage />} />
                <Route path="email" element={<AdminEmailPage />} />
              </Route>

              <Route path="/admin/billing" element={<AdminHubTabs tabs={HUB_TABS.billing} />}>
                <Route index element={<Navigate to="/admin/billing/plans" replace />} />
                <Route path="plans" element={<AdminPlansPricesPage />} />
                <Route path="coupons" element={<AdminCouponsPage />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="invoice-design" element={<AdminInvoiceDesignPage />} />
                <Route path="limits" element={<AdminPlanLimitsPage />} />
                <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
                <Route path="pricing" element={<AdminPricingPage />} />
                <Route path="promotions" element={<AdminPromotionsPage />} />
                <Route path="gamification" element={<AdminGamificationPage />} />
                <Route path="payment-gateway" element={<AdminPaymentGatewayPage />} />
                <Route path="boost" element={<AdminBoostPage />} />
              </Route>

              <Route path="/admin/storefront" element={<AdminHubTabs tabs={HUB_TABS.storefront} />}>
                <Route index element={<Navigate to="/admin/storefront/categories" replace />} />
                <Route path="categories" element={<AdminCategoriesPage />} />
                <Route path="home-deal-banners" element={<AdminHomeDealBannersPage />} />
                <Route path="chips" element={<AdminChipsPage />} />
                <Route path="promo-banners" element={<AdminPromoBannersPage />} />
              </Route>

              <Route path="/admin/content" element={<AdminHubTabs tabs={HUB_TABS.content} />}>
                <Route index element={<Navigate to="/admin/content/news" replace />} />
                <Route path="news" element={<AdminNewsPage />} />
                <Route path="webinars" element={<AdminWebinarsPage />} />
                <Route path="polls" element={<AdminPollsPage />} />
                <Route path="community" element={<AdminCommunityPage />} />
              </Route>

              <Route path="/admin/ai-config" element={<AdminHubTabs tabs={HUB_TABS.aiConfig} />}>
                <Route index element={<Navigate to="/admin/ai-config/prompts" replace />} />
                <Route path="prompts" element={<AdminPromptPlaygroundPage />} />
                <Route path="templates" element={<AdminTemplatesPage />} />
                <Route path="recommendations" element={<AdminRecommendationsPage />} />
              </Route>

              <Route path="/admin/settings" element={<AdminHubTabs tabs={HUB_TABS.settings} />}>
                <Route index element={<AdminSettingsPage />} />
                <Route path="onboarding" element={<AdminOnboardingPage />} />
                <Route path="legal" element={<AdminLegalDocsPage />} />
              </Route>

              {/* Legacy paths kept as redirects so old bookmarks, emails and
                  push-notification deep links don't dead-end. */}
              <Route path="/admin" element={<AdminPathRedirect to="/admin/insights/dashboard" />} />
              <Route path="/admin/analytics" element={<AdminPathRedirect to="/admin/insights/analytics" />} />
              <Route path="/admin/activity" element={<AdminPathRedirect to="/admin/insights/activity" />} />
              <Route path="/admin/ai-generations" element={<AdminPathRedirect to="/admin/insights/ai-generations" />} />
              <Route path="/admin/market-pulse" element={<AdminPathRedirect to="/admin/insights/market-pulse" />} />
              <Route path="/admin/ai-search/rag-chat" element={<AdminPathRedirect to="/admin/insights/rag-chat" />} />
              <Route path="/admin/ai-search/market-research" element={<AdminPathRedirect to="/admin/insights/market-research" />} />
              <Route path="/admin/last-minute-review" element={<AdminPathRedirect to="/admin/deals/last-minute-review" />} />
              <Route path="/admin/gallery-review" element={<AdminPathRedirect to="/admin/deals/gallery-review" />} />
              <Route path="/admin/stuck-deals" element={<AdminPathRedirect to="/admin/deals/stuck-deals" />} />
              <Route path="/admin/indexing" element={<AdminPathRedirect to="/admin/deals/indexing" />} />
              <Route path="/admin/notifications" element={<AdminPathRedirect to="/admin/channels/notifications" />} />
              <Route path="/admin/notification-ops" element={<AdminPathRedirect to="/admin/channels/notification-ops" />} />
              <Route path="/admin/whatsapp" element={<AdminPathRedirect to="/admin/channels/whatsapp" />} />
              <Route path="/admin/voice-agent" element={<AdminPathRedirect to="/admin/channels/voice-agent" />} />
              <Route path="/admin/email" element={<AdminPathRedirect to="/admin/channels/email" />} />
              <Route path="/admin/subscriptions" element={<AdminPathRedirect to="/admin/billing/subscriptions" />} />
              <Route path="/admin/pricing" element={<AdminPathRedirect to="/admin/billing/pricing" />} />
              <Route path="/admin/payment-gateway" element={<AdminPathRedirect to="/admin/billing/payment-gateway" />} />
              <Route path="/admin/boost" element={<AdminPathRedirect to="/admin/billing/boost" />} />
              <Route path="/admin/categories" element={<AdminPathRedirect to="/admin/storefront/categories" />} />
              <Route path="/admin/home-deal-banners" element={<AdminPathRedirect to="/admin/storefront/home-deal-banners" />} />
              <Route path="/admin/chips" element={<AdminPathRedirect to="/admin/storefront/chips" />} />
              <Route path="/admin/news" element={<AdminPathRedirect to="/admin/content/news" />} />
              <Route path="/admin/webinars" element={<AdminPathRedirect to="/admin/content/webinars" />} />
              <Route path="/admin/polls" element={<AdminPathRedirect to="/admin/content/polls" />} />
              <Route path="/admin/community" element={<AdminPathRedirect to="/admin/content/community" />} />
              <Route path="/admin/prompts" element={<AdminPathRedirect to="/admin/ai-config/prompts" />} />
              <Route path="/admin/templates" element={<AdminPathRedirect to="/admin/ai-config/templates" />} />
              <Route path="/admin/recommendations" element={<AdminPathRedirect to="/admin/ai-config/recommendations" />} />
              <Route path="/admin/onboarding-options" element={<AdminPathRedirect to="/admin/settings/onboarding" />} />
            </Route>
          </Route>

          {/* Org admin — a parallel, fully separate tree from /admin/*.
              Guarded by OrgAdminRoute (org_admin_* localStorage keys), never by
              AdminRoute, so a superadmin session grants nothing here and an
              org-admin session grants nothing in /admin/*. */}
          <Route path="/org-admin/login" element={<OrgAdminLoginPage />} />
          <Route path="/org-admin/magic-login" element={<OrgAdminMagicLoginPage />} />
          <Route element={<OrgAdminRoute />}>
            <Route element={<OrgAdminLayout />}>
              <Route path="/org-admin" element={<Navigate to="/org-admin/dashboard" replace />} />
              <Route path="/org-admin/dashboard" element={<OrgAdminDashboardPage />} />
              <Route path="/org-admin/candidates" element={<OrgAdminCandidatesPage />} />
              <Route path="/org-admin/members" element={<OrgAdminMembersPage />} />
            </Route>
          </Route>

          {/* superadmin.gullivr.ai only ever shows /admin/*; send anything else there */}
          <Route
            path="*"
            element={superAdminHost ? <Navigate to="/admin" replace /> : <NotFoundPage />}
          />
        </Routes>
        </UpgradePromptProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;

